import type {
  LocalRecordingSession,
  LocalRecordingUpload,
  LocalRecordingStatus,
  RecorderStore,
  RecorderUploadStore,
  RecordingChunk,
} from './types';

const DB_NAME = 'vd-recorder-v1';
const DB_VERSION = 1;
const UPLOAD_DB_NAME = 'vd-recorder-uploads-v1';
const UPLOAD_DB_VERSION = 1;
const SESSION_STORE = 'sessions';
const CHUNK_STORE = 'chunks';
const UPLOAD_STORE = 'uploads';

let openPromise: Promise<IDBDatabase> | null = null;
let uploadOpenPromise: Promise<IDBDatabase> | null = null;

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed.'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (openPromise) return openPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is unavailable in this browser.'));
  }
  let promise!: Promise<IDBDatabase>;
  let abandoned = false;
  promise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(SESSION_STORE)) {
        const sessions = database.createObjectStore(SESSION_STORE, { keyPath: 'id' });
        sessions.createIndex('ownerId', 'ownerId', { unique: false });
        sessions.createIndex('status', 'status', { unique: false });
      }
      if (!database.objectStoreNames.contains(CHUNK_STORE)) {
        const chunks = database.createObjectStore(CHUNK_STORE, { keyPath: 'key' });
        chunks.createIndex('sessionId', 'sessionId', { unique: false });
        chunks.createIndex('ownerId', 'ownerId', { unique: false });
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      if (abandoned) {
        database.close();
        return;
      }
      database.onversionchange = () => {
        database.close();
        if (openPromise === promise) openPromise = null;
      };
      resolve(database);
    };
    request.onerror = () => {
      if (openPromise === promise) openPromise = null;
      reject(request.error ?? new Error('Unable to open recorder storage.'));
    };
    request.onblocked = () => {
      abandoned = true;
      if (openPromise === promise) openPromise = null;
      reject(new Error('Recorder storage upgrade is blocked by another tab.'));
    };
  });
  openPromise = promise;
  return promise;
}

function openUploadDatabase(): Promise<IDBDatabase> {
  if (uploadOpenPromise) return uploadOpenPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is unavailable in this browser.'));
  }
  let promise!: Promise<IDBDatabase>;
  let abandoned = false;
  promise = new Promise((resolve, reject) => {
    const request = indexedDB.open(UPLOAD_DB_NAME, UPLOAD_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(UPLOAD_STORE)) {
        const uploads = database.createObjectStore(UPLOAD_STORE, { keyPath: 'recordingId' });
        uploads.createIndex('ownerId', 'ownerId', { unique: false });
        uploads.createIndex('status', 'status', { unique: false });
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      if (abandoned) {
        database.close();
        return;
      }
      database.onversionchange = () => {
        database.close();
        if (uploadOpenPromise === promise) uploadOpenPromise = null;
      };
      resolve(database);
    };
    request.onerror = () => {
      if (uploadOpenPromise === promise) uploadOpenPromise = null;
      reject(request.error ?? new Error('Unable to open recorder upload storage.'));
    };
    request.onblocked = () => {
      abandoned = true;
      if (uploadOpenPromise === promise) uploadOpenPromise = null;
      reject(new Error('Recorder upload storage upgrade is blocked by another tab.'));
    };
  });
  uploadOpenPromise = promise;
  return promise;
}

async function deleteSessionFromDatabase(database: IDBDatabase, sessionId: string): Promise<void> {
  const transaction = database.transaction([SESSION_STORE, CHUNK_STORE], 'readwrite');
  const done = transactionDone(transaction);
  transaction.objectStore(SESSION_STORE).delete(sessionId);
  const chunkIndex = transaction.objectStore(CHUNK_STORE).index('sessionId');
  const cursorRequest = chunkIndex.openKeyCursor(IDBKeyRange.only(sessionId));
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    transaction.objectStore(CHUNK_STORE).delete(cursor.primaryKey);
    cursor.continue();
  };
  await done;
}

async function deleteUploadFromDatabase(database: IDBDatabase, recordingId: string): Promise<void> {
  const transaction = database.transaction(UPLOAD_STORE, 'readwrite');
  const done = transactionDone(transaction);
  transaction.objectStore(UPLOAD_STORE).delete(recordingId);
  await done;
}

export class IndexedDbRecorderStore implements RecorderStore, RecorderUploadStore {
  async saveSession(session: LocalRecordingSession): Promise<void> {
    const database = await openDatabase();
    const transaction = database.transaction(SESSION_STORE, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(SESSION_STORE).put(session);
    await done;
  }

  async appendChunk(
    session: LocalRecordingSession,
    chunk: RecordingChunk,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) throw signal.reason ?? new DOMException('Recorder write aborted.', 'AbortError');
    const database = await openDatabase();
    if (signal?.aborted) throw signal.reason ?? new DOMException('Recorder write aborted.', 'AbortError');
    const transaction = database.transaction([SESSION_STORE, CHUNK_STORE], 'readwrite');
    const done = transactionDone(transaction);
    const abort = () => {
      try { transaction.abort(); } catch { /* transaction already settled */ }
    };
    signal?.addEventListener('abort', abort, { once: true });
    transaction.objectStore(CHUNK_STORE).add(chunk);
    transaction.objectStore(SESSION_STORE).put(session);
    try {
      await done;
    } finally {
      signal?.removeEventListener('abort', abort);
    }
  }

  async listRecoverable(ownerId: string, now: Date): Promise<LocalRecordingSession[]> {
    const database = await openDatabase();
    const transaction = database.transaction(SESSION_STORE, 'readonly');
    const done = transactionDone(transaction);
    let sessions: LocalRecordingSession[];
    try {
      sessions = await requestValue(
        transaction.objectStore(SESSION_STORE).index('ownerId').getAll(IDBKeyRange.only(ownerId)),
      ) as LocalRecordingSession[];
      await done;
    } catch (error) {
      await done.catch(() => {});
      throw error;
    }

    const currentMs = now.getTime();
    const recoverable: LocalRecordingSession[] = [];
    for (const session of sessions) {
      const expiresAt = Date.parse(session.expiresAt);
      if (!Number.isFinite(expiresAt) || expiresAt <= currentMs) {
        await deleteUploadFromDatabase(await openUploadDatabase(), session.id);
        await deleteSessionFromDatabase(database, session.id);
        continue;
      }
      recoverable.push(session);
    }
    return recoverable.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async markStatus(
    id: string,
    status: LocalRecordingStatus,
    elapsedMs?: number,
  ): Promise<LocalRecordingSession | null> {
    const database = await openDatabase();
    const transaction = database.transaction(SESSION_STORE, 'readwrite');
    const done = transactionDone(transaction);
    const store = transaction.objectStore(SESSION_STORE);
    let existing: LocalRecordingSession | undefined;
    try {
      existing = await requestValue(store.get(id)) as LocalRecordingSession | undefined;
    } catch (error) {
      await done.catch(() => {});
      throw error;
    }
    if (!existing) {
      await done;
      return null;
    }
    const updated: LocalRecordingSession = {
      ...existing,
      status,
      elapsedMs: elapsedMs ?? existing.elapsedMs,
      updatedAt: new Date().toISOString(),
    };
    store.put(updated);
    await done;
    return updated;
  }

  async deleteSession(id: string): Promise<void> {
    await deleteUploadFromDatabase(await openUploadDatabase(), id);
    await deleteSessionFromDatabase(await openDatabase(), id);
  }

  async readChunks(recordingId: string): Promise<RecordingChunk[]> {
    return readRecordingChunks(recordingId);
  }

  async deleteChunks(recordingId: string): Promise<void> {
    return deleteRecordingChunks(recordingId);
  }

  async saveUpload(upload: LocalRecordingUpload): Promise<void> {
    const database = await openUploadDatabase();
    const transaction = database.transaction(UPLOAD_STORE, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(UPLOAD_STORE).put(upload);
    await done;
  }

  async getUpload(recordingId: string): Promise<LocalRecordingUpload | null> {
    const database = await openUploadDatabase();
    const transaction = database.transaction(UPLOAD_STORE, 'readonly');
    const done = transactionDone(transaction);
    const value = await requestValue(transaction.objectStore(UPLOAD_STORE).get(recordingId))
      .catch(async (error) => {
        await done.catch(() => {});
        throw error;
      });
    await done;
    return validLocalUpload(value) ? value : null;
  }

  async listUploads(ownerId: string): Promise<LocalRecordingUpload[]> {
    const database = await openUploadDatabase();
    const transaction = database.transaction(UPLOAD_STORE, 'readonly');
    const done = transactionDone(transaction);
    const values = await requestValue(
      transaction.objectStore(UPLOAD_STORE).index('ownerId').getAll(IDBKeyRange.only(ownerId)),
    ).catch(async (error) => {
      await done.catch(() => {});
      throw error;
    });
    await done;
    return (values as unknown[]).filter(validLocalUpload)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async deleteUpload(recordingId: string): Promise<void> {
    await deleteUploadFromDatabase(await openUploadDatabase(), recordingId);
  }
}

/** Clear local recorder artifacts during explicit sign-out or a 401 reset. */
export async function clearRecorderData(ownerId?: string): Promise<void> {
  const database = await openDatabase();
  const uploadDatabase = await openUploadDatabase();
  if (!ownerId) {
    const transaction = database.transaction([SESSION_STORE, CHUNK_STORE], 'readwrite');
    const uploadTransaction = uploadDatabase.transaction(UPLOAD_STORE, 'readwrite');
    const done = transactionDone(transaction);
    const uploadDone = transactionDone(uploadTransaction);
    transaction.objectStore(SESSION_STORE).clear();
    transaction.objectStore(CHUNK_STORE).clear();
    uploadTransaction.objectStore(UPLOAD_STORE).clear();
    await Promise.all([done, uploadDone]);
    return;
  }
  const transaction = database.transaction(SESSION_STORE, 'readonly');
  const done = transactionDone(transaction);
  let keys: IDBValidKey[];
  try {
    keys = await requestValue(
      transaction.objectStore(SESSION_STORE).index('ownerId').getAllKeys(IDBKeyRange.only(ownerId)),
    );
    await done;
  } catch (error) {
    await done.catch(() => {});
    throw error;
  }
  const uploadTransaction = uploadDatabase.transaction(UPLOAD_STORE, 'readonly');
  const uploadDone = transactionDone(uploadTransaction);
  let uploadKeys: IDBValidKey[];
  try {
    uploadKeys = await requestValue(
      uploadTransaction.objectStore(UPLOAD_STORE).index('ownerId').getAllKeys(IDBKeyRange.only(ownerId)),
    );
    await uploadDone;
  } catch (error) {
    await uploadDone.catch(() => {});
    throw error;
  }
  for (const key of uploadKeys) await deleteUploadFromDatabase(uploadDatabase, String(key));
  for (const key of keys) await deleteSessionFromDatabase(database, String(key));
}

/**
 * Sign-out requires stronger semantics than a best-effort store clear. If a
 * transaction fails, close our connection and delete the recorder database so
 * a later account on the same machine cannot recover screen or voice data.
 */
export async function purgeRecorderDataForSignOut(): Promise<void> {
  try {
    await clearRecorderData();
  } catch {
    const database = openPromise ? await openPromise.catch(() => null) : null;
    const uploadDatabase = uploadOpenPromise ? await uploadOpenPromise.catch(() => null) : null;
    database?.close();
    uploadDatabase?.close();
    openPromise = null;
    uploadOpenPromise = null;
    if (typeof indexedDB === 'undefined') return;
    await Promise.all([
      requestValue(indexedDB.deleteDatabase(DB_NAME)),
      requestValue(indexedDB.deleteDatabase(UPLOAD_DB_NAME)),
    ]);
  }
}

export async function readRecordingChunks(sessionId: string): Promise<RecordingChunk[]> {
  const database = await openDatabase();
  const transaction = database.transaction(CHUNK_STORE, 'readonly');
  const done = transactionDone(transaction);
  let chunks: RecordingChunk[];
  try {
    chunks = await requestValue(
      transaction.objectStore(CHUNK_STORE).index('sessionId').getAll(IDBKeyRange.only(sessionId)),
    ) as RecordingChunk[];
    await done;
  } catch (error) {
    await done.catch(() => {});
    throw error;
  }
  return chunks.sort((a, b) => a.sequence - b.sequence);
}

export async function deleteRecordingChunks(sessionId: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(CHUNK_STORE, 'readwrite');
  const done = transactionDone(transaction);
  const store = transaction.objectStore(CHUNK_STORE);
  const cursorRequest = store.index('sessionId').openKeyCursor(IDBKeyRange.only(sessionId));
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    store.delete(cursor.primaryKey);
    cursor.continue();
  };
  await done;
}

function validLocalUpload(value: unknown): value is LocalRecordingUpload {
  if (!value || typeof value !== 'object') return false;
  const upload = value as Partial<LocalRecordingUpload>;
  return typeof upload.recordingId === 'string'
    && upload.recordingId.length > 0
    && typeof upload.ownerId === 'string'
    && upload.ownerId.length > 0
    && ['pending', 'uploading', 'processing', 'cleanup', 'ready', 'failed'].includes(upload.status ?? '')
    && Number.isSafeInteger(upload.offset)
    && (upload.offset ?? -1) >= 0
    && Number.isSafeInteger(upload.totalBytes)
    && (upload.totalBytes ?? 0) > 0
    && (upload.offset ?? Number.MAX_SAFE_INTEGER) <= (upload.totalBytes ?? -1)
    && Number.isSafeInteger(upload.attempts)
    && (upload.attempts ?? -1) >= 0
    && typeof upload.createdAt === 'string'
    && Number.isFinite(Date.parse(upload.createdAt))
    && typeof upload.updatedAt === 'string'
    && Number.isFinite(Date.parse(upload.updatedAt));
}

/** Test-only database reset. Production code should purge through clearRecorderData. */
export async function resetRecorderDatabaseForTests(): Promise<void> {
  const database = openPromise ? await openPromise.catch(() => null) : null;
  const uploadDatabase = uploadOpenPromise ? await uploadOpenPromise.catch(() => null) : null;
  database?.close();
  uploadDatabase?.close();
  openPromise = null;
  uploadOpenPromise = null;
  if (typeof indexedDB === 'undefined') return;
  await Promise.all([
    requestValue(indexedDB.deleteDatabase(DB_NAME)),
    requestValue(indexedDB.deleteDatabase(UPLOAD_DB_NAME)),
  ]);
}
